<script setup>
import {
  computed,
  onMounted,
  ref,
  watch,
  provide,
  defineAsyncComponent,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import PilotKnowledgeBasesAPI from 'dashboard/api/pilot/knowledgeBases';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import WechatCrawlRuleDrawer from './WechatCrawlRuleDrawer.vue';
import CreateKnowledgeBaseDialog from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/CreateKnowledgeBaseDialog.vue';

// 异步加载子组件（按需加载，优化性能）
const WechatCrawlEntryTab = defineAsyncComponent(
  () => import('./components/WechatCrawlEntryTab.vue')
);
const WechatCrawlRulesTab = defineAsyncComponent(
  () => import('./components/WechatCrawlRulesTab.vue')
);
const WechatCrawlHistoryTab = defineAsyncComponent(
  () => import('./components/WechatCrawlHistoryTab.vue')
);
const WechatCrawlDocsTab = defineAsyncComponent(
  () => import('./components/WechatCrawlDocsTab.vue')
);

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

// 提供共享数据给子组件（inject 使用）
provide('store', store);
provide('route', route);
provide('router', router);

// Tab 名称映射（与路由参数对应）
const TAB_NAMES = ['entry', 'rules', 'history', 'docs'];

// 从 URL 路径参数获取初始 tab
const getInitialTab = () => {
  const pageParam = route.params.page;
  const tabIndex = TAB_NAMES.indexOf(pageParam);
  return tabIndex !== -1 ? tabIndex : 0;
};

// Tab 切换
const activeTab = ref(getInitialTab());
const tabs = computed(() => [
  { label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.TABS.ENTRY') },
  { label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.TABS.RULES') },
  { label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.TABS.HISTORY') },
  { label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.TABS.DOCS') },
]);

const handleTabChanged = tab => {
  const tabIndex = tabs.value.indexOf(tab);
  if (tabIndex !== -1) {
    activeTab.value = tabIndex;
    // 更新 URL 路径
    router.push({
      name: 'pilot_global_documents_wechat_crawl_page',
      params: { page: TAB_NAMES[tabIndex] },
    });
  }
};

// 导航到指定 tab
const navigateToTab = tabIndex => {
  if (tabIndex >= 0 && tabIndex < TAB_NAMES.length) {
    activeTab.value = tabIndex;
    router.push({
      name: 'pilot_global_documents_wechat_crawl_page',
      params: { page: TAB_NAMES[tabIndex] },
    });
  }
};

// 监听路由参数变化，同步更新 activeTab
watch(
  () => route.params.page,
  newPage => {
    const tabIndex = TAB_NAMES.indexOf(newPage);
    if (tabIndex !== -1 && activeTab.value !== tabIndex) {
      activeTab.value = tabIndex;
    }
  }
);

// 基础 URL
const baseUrl = computed(() => window.location.origin);
const accountId = computed(() => router.currentRoute.value.params.accountId);

// 外部服务 URL（从 store 获取）
const outboundServiceUrl = computed(
  () => store.state.pilotWechatCrawlRunMode?.outboundServiceUrl || ''
);

// 提供共享数据给子组件
provide('wechatCrawl.baseUrl', baseUrl);
provide('wechatCrawl.accountId', accountId);
provide('wechatCrawl.outboundServiceUrl', outboundServiceUrl);

// ==================== 知识库和规则相关 ====================

// 知识库列表
const knowledgeBases = ref([]);
const globalStats = ref({ pending: 0, success: 0, failed: 0, filtered: 0 });

// 加载状态
const isLoading = ref(false);
const isLoadingMore = ref(false);

// 分页相关
const PAGE_SIZE = 25;
const currentPage = ref(1);

// 当前显示的知识库列表
const displayedKnowledgeBases = computed(() => {
  return knowledgeBases.value.slice(0, currentPage.value * PAGE_SIZE);
});

// 是否还有更多数据
const hasMoreData = computed(() => {
  return displayedKnowledgeBases.value.length < knowledgeBases.value.length;
});

// 加载更多
const loadMore = () => {
  if (hasMoreData.value && !isLoadingMore.value) {
    isLoadingMore.value = true;
    // 模拟加载延迟，让用户看到加载状态
    setTimeout(() => {
      currentPage.value += 1;
      isLoadingMore.value = false;
    }, 300);
  }
};

// 抽屉引用
const ruleDrawerRef = ref(null);
const selectedKbRule = ref(null);
const deleteDialogRef = ref(null);
const kbToDelete = ref(null);

// 获取分流模式下已启用的知识库列表
const enabledKnowledgeBases = computed(() => {
  return knowledgeBases.value.filter(kb => kb.hasRule && kb.rule?.enabled);
});

// 创建知识库弹窗引用
const createKnowledgeBaseDialogRef = ref(null);

// 抓取确认弹窗引用
const noRulesDialogRef = ref(null);
const confirmCrawlDialogRef = ref(null);

// 删除确认弹窗的描述文案
const deleteConfirmDescription = computed(() => {
  const name = kbToDelete.value?.name || '';
  return t(
    'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.CLEAR_RULE_CONFIRM_MESSAGE',
    { name }
  );
});

// 获取所有知识库（并附带规则信息）
const fetchKnowledgeBases = async () => {
  isLoading.value = true;
  currentPage.value = 1; // 重置分页
  try {
    // 获取所有页面的知识库数据
    let allKbs = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await PilotKnowledgeBasesAPI.get({ page });
      const kbs = response.data.payload || [];

      allKbs = [...allKbs, ...kbs];

      // 如果返回的数据少于每页数量，说明没有更多数据了
      hasMore = kbs.length >= 25;
      page++;
    }

    const kbsWithRules = [];

    for (const kb of allKbs) {
      // 尝试获取该知识库的规则
      let ruleData = null;
      let ruleStats = { pending: 0, success: 0, failed: 0, filtered: 0 };
      try {
        const ruleResponse = await store.dispatch('pilotWechatCrawl/show', {
          knowledge_base_id: kb.id,
        });
        if (ruleResponse?.rule) {
          ruleData = {
            id: ruleResponse.rule.id,
            enabled: ruleResponse.rule.enabled !== false,
            mode: ruleResponse.rule.mode || 'keyword',
            keywords: ruleResponse.rule.keywords || [],
            matchMode: ruleResponse.rule.match_mode || 'any',
          };
          ruleStats = ruleResponse.stats || {
            pending: 0,
            success: 0,
            failed: 0,
            filtered: 0,
          };
        }
      } catch (error) {
        // 该知识库没有规则
      }

      kbsWithRules.push({
        id: kb.id,
        name: kb.name,
        logoUrl: kb.logo_url,
        logo_url: kb.logo_url,
        description: kb.description,
        documentsCount: kb.documents_count || 0,
        documents_count: kb.documents_count || 0,
        faqsCount: kb.faqs_count || 0,
        faqs_count: kb.faqs_count || 0,
        hasRule: !!ruleData,
        rule: ruleData || {
          id: null,
          enabled: false,
          mode: 'keyword',
          keywords: [],
          matchMode: 'any',
        },
        stats: ruleStats,
      });
    }

    knowledgeBases.value = kbsWithRules;
  } catch (error) {
    console.error('Failed to fetch knowledge bases:', error);
    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// 获取规则预览文本
const getRulePreview = rule => {
  // 开放模式
  if (rule?.mode === 'open') {
    return t(
      'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.MODE_OPEN_PREVIEW'
    );
  }
  // 关键词模式
  const keywords = rule?.keywords || [];
  if (keywords.length === 0) {
    return t(
      'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.NO_KEYWORDS'
    );
  }
  const modeLabel = t(
    'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.MODE_KEYWORD_PREVIEW'
  );
  const preview = keywords.slice(0, 3).join(', ');
  const keywordStr =
    keywords.length > 3
      ? `${preview}... (${keywords.length}${t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.KEYWORDS_UNIT')})`
      : `${preview} (${keywords.length}${t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.KEYWORDS_UNIT')})`;
  return `${modeLabel} - ${keywordStr}`;
};

// 打开规则抽屉
const openRuleDrawer = kb => {
  // 映射属性名以匹配抽屉组件期望的结构
  selectedKbRule.value = {
    knowledgeBaseId: kb.id,
    knowledgeBaseName: kb.name,
    logoUrl: kb.logoUrl,
    description: kb.description,
    hasRule: kb.hasRule,
    rule: kb.rule,
    stats: kb.stats,
  };
  ruleDrawerRef.value?.open();
};

// 切换规则启用状态
const toggleRuleEnabled = async kb => {
  const newEnabled = !kb.rule.enabled;

  // 开启开关：没有规则时打开抽屉让用户配置
  if (newEnabled && !kb.hasRule) {
    openRuleDrawer(kb);
    return;
  }

  // 直接开启/关闭
  kb.rule.enabled = newEnabled;

  try {
    if (kb.hasRule && kb.rule.id) {
      await store.dispatch('pilotWechatCrawl/update', {
        knowledge_base_id: kb.id,
        wechat_crawl: {
          enabled: newEnabled,
          config: {
            mode: kb.rule.mode || 'keyword',
            keywords: kb.rule.keywords || [],
            match_mode: kb.rule.matchMode || 'any',
          },
        },
      });
    }
    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.SAVE_SUCCESS'));
  } catch (error) {
    // 恢复原状态
    kb.rule.enabled = !newEnabled;
    console.error('Failed to toggle rule:', error);
    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.SAVE_FAILED'));
  }
};

// 处理抽屉保存
const handleDrawerSave = updatedRule => {
  // 移除 isNew 标记
  const { isNew, ...ruleWithoutFlag } = updatedRule;

  const index = knowledgeBases.value.findIndex(
    r => r.id === updatedRule.knowledgeBaseId
  );

  if (index !== -1) {
    // 更新已存在的知识库规则
    knowledgeBases.value[index] = {
      ...knowledgeBases.value[index],
      hasRule: true,
      rule: ruleWithoutFlag.rule,
      stats: ruleWithoutFlag.stats,
    };
  }

  // 更新全局统计
  recalculateStats();
};

// 处理抽屉清空规则
const handleDrawerClear = clearedRule => {
  const index = knowledgeBases.value.findIndex(
    r => r.id === clearedRule.knowledgeBaseId
  );

  if (index !== -1) {
    // 清空规则信息，但保留知识库
    knowledgeBases.value[index] = {
      ...knowledgeBases.value[index],
      hasRule: false,
      rule: {
        id: null,
        enabled: false,
        mode: 'keyword',
        keywords: [],
        matchMode: 'any',
      },
      stats: { pending: 0, success: 0, failed: 0, filtered: 0 },
    };
  }

  // 重新计算全局统计
  recalculateStats();
};

// 确认删除知识库规则
const confirmRemoveKb = kbRule => {
  kbToDelete.value = kbRule;
  deleteDialogRef.value?.open();
};

// 执行删除知识库规则
const handleRemoveKb = async () => {
  if (kbToDelete.value) {
    try {
      // 调用 API 删除规则
      await store.dispatch('pilotWechatCrawl/delete', kbToDelete.value.id);

      const index = knowledgeBases.value.findIndex(
        r => r.id === kbToDelete.value.id
      );
      if (index !== -1) {
        // 清除规则信息，但保留知识库
        knowledgeBases.value[index] = {
          ...knowledgeBases.value[index],
          hasRule: false,
          rule: {
            id: null,
            enabled: false,
            mode: 'keyword',
            keywords: [],
            matchMode: 'any',
          },
          stats: { pending: 0, success: 0, failed: 0, filtered: 0 },
        };
      }
      recalculateStats();
      useAlert(
        t(
          'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.CLEAR_SUCCESS'
        )
      );
    } catch (error) {
      console.error('Failed to clear rule:', error);
      useAlert(
        t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.CLEAR_FAILED')
      );
    } finally {
      kbToDelete.value = null;
      deleteDialogRef.value?.close();
    }
  }
};

// 重新计算全局统计
const recalculateStats = () => {
  const totalStats = { pending: 0, success: 0, failed: 0, filtered: 0 };
  for (const kb of knowledgeBases.value) {
    if (kb.hasRule) {
      totalStats.pending += kb.stats?.pending || 0;
      totalStats.success += kb.stats?.success || 0;
      totalStats.failed += kb.stats?.failed || 0;
      totalStats.filtered += kb.stats?.filtered || 0;
    }
  }
  globalStats.value = totalStats;
};

// ==================== 入口 Tab 事件处理 ====================

// 打开外部服务
const openExternalService = () => {
  if (enabledKnowledgeBases.value.length === 0) {
    // 未激活，没有配置规则
    noRulesDialogRef.value?.open();
  } else {
    // 已激活，显示确认弹窗
    confirmCrawlDialogRef.value?.open();
  }
};

// 打开无规则弹窗后跳转到图文接收知识库
const goToRulesTab = () => {
  noRulesDialogRef.value?.close();
  navigateToTab(1);
};

// 打开规则抽屉查看规则详情
const openRuleDetail = kb => {
  confirmCrawlDialogRef.value?.close();
  openRuleDrawer(kb);
};

// 确认抓取，跳转到外部服务
const confirmCrawl = async () => {
  confirmCrawlDialogRef.value?.close();
  try {
    const data = await store.dispatch(
      'pilotWechatCrawlRunMode/generateCrawlUrl'
    );
    if (data.url) {
      window.open(data.url, '_blank');
    }
  } catch (error) {
    console.error('Failed to generate crawl URL:', error);
    useAlert(
      t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.GENERATE_URL_ERROR')
    );
  }
};

// ==================== 通用事件处理 ====================

// 返回列表页
const goBack = () => {
  router.push({
    name: 'pilot_knowledge_bases_index',
  });
};

// 打开创建知识库弹窗
const openCreateKnowledgeBaseDialog = () => {
  createKnowledgeBaseDialogRef.value?.open();
};

// 关闭创建知识库弹窗
const handleCreateDialogClose = () => {
  // 弹窗关闭时不需要额外处理
};

// 创建知识库成功后刷新列表
const handleCreateSuccess = () => {
  fetchKnowledgeBases();
};

// 获取全局统计数据（从抓取文章 API 获取）
const fetchGlobalStats = async () => {
  try {
    const response = await store.dispatch('pilotWechatCrawlArticles/fetch', {
      page: 1,
      per_page: 1, // 只需要统计信息，不需要数据
    });
    if (response?.meta?.status_counts) {
      globalStats.value = {
        pending: response.meta.status_counts.pending || 0,
        success: response.meta.status_counts.success || 0,
        failed: response.meta.status_counts.failed || 0,
        filtered: response.meta.status_counts.filtered || 0,
      };
    }
  } catch (error) {
    console.error('Failed to fetch global stats:', error);
  }
};

onMounted(async () => {
  // 获取运行模式状态
  await store.dispatch('pilotWechatCrawlRunMode/fetch');
  // 获取知识库列表
  fetchKnowledgeBases();
  // 获取全局统计
  fetchGlobalStats();
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-slate-white dark:bg-n-slate-800">
    <!-- Header -->
    <header class="sticky top-0 z-10 px-6 bg-n-slate-white dark:bg-n-slate-800">
      <div class="w-full mx-auto max-w-[60rem]">
        <div class="flex items-center justify-between h-20">
          <div class="flex items-center">
            <button
              type="button"
              class="p-2 -ml-2 text-n-slate-600 dark:text-n-slate-400 hover:bg-n-slate-100 dark:hover:bg-n-slate-700 rounded-lg transition-colors"
              @click="goBack"
            >
              <Icon icon="i-lucide-arrow-left" class="h-5 w-5" />
            </button>
            <h1
              class="ml-4 text-lg font-semibold text-n-slate-900 dark:text-n-slate-100"
            >
              {{ t('PILOT.KNOWLEDGE_BASES.SIDEBAR.WECHAT_CRAWL_TITLE') }}
            </h1>
          </div>
          <!-- Tab Bar -->
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTab"
            @tab-changed="handleTabChanged"
          />
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex-1 w-full px-6 overflow-y-auto">
      <div class="w-full mx-auto max-w-[60rem] py-6 flex flex-col gap-6">
        <!-- Tab: 抓取入口 -->
        <template v-if="activeTab === 0">
          <WechatCrawlEntryTab
            :global-stats="globalStats"
            @navigate-to-rules="() => navigateToTab(1)"
            @open-external-service="openExternalService"
          />
        </template>

        <!-- Tab: 图文接收知识库 -->
        <template v-else-if="activeTab === 1">
          <WechatCrawlRulesTab
            :active="activeTab === 1"
            :knowledge-bases="knowledgeBases"
            :global-stats="globalStats"
            :is-loading="isLoading"
            :displayed-knowledge-bases="displayedKnowledgeBases"
            :has-more-data="hasMoreData"
            :is-loading-more="isLoadingMore"
            @load-more="loadMore"
            @open-rule-drawer="openRuleDrawer"
            @toggle-rule-enabled="toggleRuleEnabled"
            @confirm-remove-kb="confirmRemoveKb"
            @open-create-knowledge-base="openCreateKnowledgeBaseDialog"
          />
        </template>

        <!-- Tab: 历史记录 -->
        <template v-else-if="activeTab === 2">
          <WechatCrawlHistoryTab :active="activeTab === 2" />
        </template>

        <!-- Tab: 开发文档 -->
        <template v-else-if="activeTab === 3">
          <WechatCrawlDocsTab />
        </template>
      </div>
    </main>

    <!-- 规则配置抽屉 -->
    <WechatCrawlRuleDrawer
      ref="ruleDrawerRef"
      :kb-rule="selectedKbRule"
      :is-kb-locked="true"
      @save="handleDrawerSave"
      @clear="handleDrawerClear"
    />

    <!-- 删除确认弹窗 -->
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="
        t(
          'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.CLEAR_RULE_CONFIRM_TITLE'
        )
      "
      :description="deleteConfirmDescription"
      :confirm-button-label="
        t(
          'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.CLEAR_RULE_CONFIRM_BUTTON'
        )
      "
      @confirm="handleRemoveKb"
    />

    <!-- 无规则提示弹窗 -->
    <Dialog
      ref="noRulesDialogRef"
      type="alert"
      :title="
        t(
          'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.NO_RULES_DIALOG_TITLE'
        )
      "
      :description="
        t(
          'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.NO_RULES_DIALOG_DESCRIPTION'
        )
      "
      :confirm-button-label="
        t(
          'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.NO_RULES_DIALOG_BUTTON'
        )
      "
      @confirm="goToRulesTab"
    />

    <!-- 抓取确认弹窗 -->
    <Dialog
      ref="confirmCrawlDialogRef"
      type="confirm"
      size="sm"
      :title="
        t(
          'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.CONFIRM_CRAWL_DIALOG_TITLE'
        )
      "
    >
      <template #default>
        <div class="space-y-4">
          <p class="text-sm text-n-slate-11">
            {{
              t(
                'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.CONFIRM_CRAWL_MULTIPLE_MODE_DESC'
              )
            }}
          </p>
          <!-- 显示已启用的知识库列表 -->
          <div class="max-h-[20rem] space-y-2 overflow-y-auto">
            <button
              v-for="kb in enabledKnowledgeBases"
              :key="kb.id"
              class="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-n-slate-2 dark:hover:bg-n-slate-6 transition-colors text-left"
              @click="openRuleDetail(kb)"
            >
              <div
                class="w-8 h-8 rounded bg-n-slate-2 dark:bg-n-slate-6 flex items-center justify-center flex-shrink-0 overflow-hidden"
              >
                <img
                  v-if="kb.logoUrl || kb.logo_url"
                  :src="kb.logoUrl || kb.logo_url"
                  :alt="kb.name"
                  class="w-full h-full object-cover"
                />
                <span v-else class="text-sm font-semibold text-n-slate-7">
                  {{ kb.name?.charAt(0) || '?' }}
                </span>
              </div>
              <div class="flex-1 min-w-0">
                <span
                  class="text-sm font-medium text-n-slate-12 truncate block"
                >
                  {{ kb.name }}
                </span>
                <span class="text-xs text-n-slate-11 truncate block">
                  {{ getRulePreview(kb.rule) }}
                </span>
              </div>
              <Icon
                icon="i-lucide-chevron-right"
                class="text-n-slate-10 size-4 flex-shrink-0"
              />
            </button>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end gap-2">
          <Button
            :label="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.CANCEL')"
            variant="faded"
            color="slate"
            @click="confirmCrawlDialogRef?.close()"
          />
          <Button
            :label="
              t(
                'PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.UNIVERSAL.CONFIRM_CRAWL_BUTTON'
              )
            "
            blue
            @click="confirmCrawl"
          />
        </div>
      </template>
    </Dialog>

    <!-- 创建知识库弹窗 -->
    <CreateKnowledgeBaseDialog
      ref="createKnowledgeBaseDialogRef"
      @close="handleCreateDialogClose"
      @success="handleCreateSuccess"
    />
  </div>
</template>
